<template>
  <transition name="modal">
    <div class="modal-fullwrapper"
         :class="[`variant-${modalVariant}`, { 'is-visible': showModal }]"
    >
      <div
        :id="id"
        class="modal-mask"
        :class="[`variant-${modalVariant}`, { 'is-visible': showModal }]"
        @click="escapeByMask"
      >
      </div>
      <div class="modal-container" @click.stop>
        <button
          class="modal-close"
          v-if="!noCloseButton"
          @click="toggleModal(false)"
        >
          <font-awesome-icon class="close__icon" icon="times-circle" />
        </button>

        <template v-if="isIframe">
          <iframe :src="iframeSrc" frameborder="0" />
        </template>

        <template v-else>
          <div class="modal-header" v-if="$slots['header']">
            <slot name="header"></slot>
          </div>

          <div class="modal-body" v-if="$slots['default']">
            <slot />
          </div>

          <div class="modal-footer" v-if="$slots['footer']">
            <slot name="footer"></slot>
          </div>
        </template>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'Modal',
  props: {
    id: {
      type: [String, Number],
      required: true
    },
    variant: {
      type: String,
      default: 'default'
    },
    iframeUrl: {
      type: String,
      default: ''
    },
    noCloseButton: {
      type: Boolean,
      default: false
    },
    noEscapeByMask: {
      type: Boolean,
      default: false
    },
    noEscapeByKeyboard: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      availableVariants: ['default', 'iframe', 'big'],
      showModal: false,
      iframeSrc: this.iframeUrl
    }
  },
  computed: {
    modalVariant() {
      return this.availableVariants.includes(this.variant)
        ? this.variant
        : this.availableVariants[0]
    },
    isIframe() {
      return this.modalVariant === 'iframe'
    }
  },
  methods: {
    duplicateModalIdCheck() {
      this.$root.__modalIds = this.$root.__modalIds = []

      if (this.$root.__modalIds.includes(this.id)) {
        console.warn(
          `Modal Id "${this.id}" is used multiple times. Use unique Ids you fool!`
        )
      }

      this.$root.__modalIds.push(this.id)
    },
    toggleModal(state) {
      this.showModal = state === true
      this.$store.commit('ui/setModalStatus', this.showModal)
    },
    escapeByMask(e) {
      if (!this.noEscapeByMask) this.toggleModal(false)
    },
    escapeByKeyboard(e) {
      if (this.showModal && !this.noEscapeByKeyboard && e.keyCode === 27) this.toggleModal(false)
    }
  },
  created() {
    this.duplicateModalIdCheck()
    this.$root.$on(`modal:${this.id}:show`, () => this.toggleModal(true))
    this.$root.$on(`modal:${this.id}:hide`, () => this.toggleModal(false))

    if (!this.noEscapeByKeyboard)
      document.body.addEventListener('keydown', this.escapeByKeyboard, true)
  },
  beforeDestroy() {
    if (!this.noEscapeByKeyboard)
      document.body.removeEventListener('keydown', this.escapeByKeyboard, true)
  },
  watch: {
    '$props.iframeUrl'(newUrl) {
      this.iframeSrc = newUrl
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/css/variables.scss';

$modal-mask-bg: rgba($brand-black, 0.5) !default;

$modal-close-size: 1.5em !default;
$modal-close-color: $brand-primary !default;

$modal-content-width: 60vw !default;
$modal-content-max-width: 640px !default;
$modal-content-bg: $brand-white !default;
$modal-content-color: $brand-darkgray !default;
$modal-content-border-radius: 3px !default;
$modal-content-box-shadow: 0 2px 8px 0 rgba($brand-black, 0.15) !default;

$modal-content-item-padding: 2rem !default;

$modal-mobile-content-margin: $grid-gutter * 1.5 !default;
$modal-mobile-content-width: 100% !default;

$modal-iframe-padding: 1.5em !default;

.modal-fullwrapper {
  display: none;
  z-index: 19999;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  position: fixed;

  &.is-visible {
    display: block !important;
  }

    .modal-container {
      position: fixed;
      z-index: 20001;
      top: 50%;
      left: 50%;
      transform: translate(-50%,-50%);
      width: 90vw;
      margin: 0;
      max-width: 600px;

      background-color: $modal-content-bg;
      color: $modal-content-color;
      border-radius: $modal-content-border-radius;
      box-shadow: $modal-content-box-shadow;

      .modal-close {
        display: block;
        position: absolute;
        top: 0;
        right: 0;
        padding: 0;
        width: $modal-close-size;
        height: $modal-close-size;
        background-color: $modal-content-bg;
        border: 0 none;
        font-size: 1rem;
        color: $modal-close-color;
        line-height: 1;
        border-radius: 50%;
        transform: translate(50%, -50%);
        cursor: pointer;

        .close__icon {
          display: block;
          margin: -1px;
          width: calc(100% + 2px);
          height: calc(100% + 2px);
          font-size: $modal-close-size;
        }
      }

      .modal-header,
      .modal-body,
      .modal-footer {
        padding: $modal-content-item-padding;
        text-align: center;

        > *:first-child {
          margin-top: 0;
        }

        > *:last-child {
          margin-bottom: 0;
        }
      }
    }

  &.variant-iframe {
      .modal-container {
        width: 100%;
        max-width: none;
        height: 100%;

        .modal-close {
          top: 20px;
          right: 30px;
        }

        .modal-header,
        .modal-body,
        .modal-footer {
          display: none !important;
        }

        iframe {
          width: 100%;
          height: 100%;
          border: 0 none;
        }
      }
  }

  &.variant-big {
    .modal-container {
      max-width: none;
      height: 90vh;
      overflow-y: scroll;
    }
    .modal-header,
    .modal-body,
    .modal-footer {
      text-align: left;

    }
  }

  .md &,
  .sm &,
  .xs & {
    &.variant-iframe {
      .modal-container {
        margin: 0;
        width: 100%;
      }
    }
  }

}

.modal-mask {
  display: none !important;
  position: fixed;
  z-index: 20000;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background-color: $modal-mask-bg;
  pointer-events: none;

  &.is-visible {
    display: block !important;
  }

}
</style>
